/**
 * Types and constants shared by the Lambda handlers, the onboarding core, the CLI, and the CDK
 * constructs.
 *
 * Dependencies point *into* this file only. It must never import aws-cdk-lib, or the Lambda bundles
 * would carry the whole CDK library.
 */

/** DynamoDB table holding the app client registry. */
export const REGISTRY_TABLE_NAME = 'broker-app-registry';

/** GSI used to resolve a friendly app name to its Cognito client ID. */
export const APP_NAME_INDEX = 'app_name-index';

/** A row in the broker-app-registry table. */
export interface AppRecord {
  /** Cognito app client ID. Partition key. */
  client_id: string;
  /** Human-readable app name, e.g. "forward-summit". Unique; GSI partition key. */
  app_name: string;
  /** Callback URLs, mirroring the app client's configured callbacks. */
  redirect_uris: string[];
  /**
   * Sign-out URLs, mirroring the app client's configured LogoutURLs.
   *
   * Cognito's `/logout` endpoint only honours a `logout_uri` that is registered here, so an app with
   * none of these can end its own session but cannot clear the broker's session cookie.
   *
   * Optional, and empty is a supported configuration: rows written before this field existed have
   * none, and an app that never calls `/logout` needs none.
   */
  logout_uris?: string[];
  /**
   * Entra group object IDs. **Empty means no group check**: any user who successfully
   * authenticates against Entra may sign in to this app. Non-empty means the user must be a
   * transitive member of at least one of these groups.
   */
  required_group_ids: string[];
  /**
   * Free-text operator notes about this app: what it is, who asked for it, and - when
   * required_group_ids is empty - why it is open to the whole tenant.
   *
   * Optional at the type level because rows written before the field existed have none, and because
   * the CLI does not require it. The admin console does: see assertUnrestrictedIsExplained.
   */
  notes?: string;
  /** Kill switch. False denies all logins for this app without deleting the client. */
  enabled: boolean;
  created_at: string;
  updated_at: string;
  /**
   * Who last wrote this row: `ui:<email>` from the admin console, `cli:<principal>` from the CLI.
   *
   * Optional forever. Rows written before audit tracking existed have no value and are deliberately
   * not backfilled - a backfill would attribute every historical change to whoever ran it.
   */
  updated_by?: string;
}

/** Shape of the JSON stored in the signing-key secret by the cert custom resource. */
export interface SigningKeyMaterial {
  privateKeyPem: string;
  certPem: string;
  /** Base64 DER - the form Entra's App Registration keyCredentials upload expects. */
  certBase64Der: string;
  /** Uppercase hex SHA-1 fingerprint. Base64url of the raw bytes becomes the JWT `x5t` header. */
  thumbprintSha1: string;
  thumbprintSha256: string;
  /** ISO 8601 certificate expiry. */
  notAfter: string;
}

/**
 * The subset of the signing material needed to sign a Graph client assertion.
 *
 * Split out from SigningKeyMaterial because signing needs two of its six fields, and a reader that
 * only validates those two must not hand back a value typed as though it had checked all six. The
 * secret is written whole by the cert custom resource, so in practice the rest is there - but
 * "in practice" is not what a type asserts.
 */
export type AssertionSigningKey = Pick<SigningKeyMaterial, 'privateKeyPem' | 'thumbprintSha1'>;

/** Shape of the JSON stored in the Entra client-secret secret. */
export interface EntraClientSecretMaterial {
  client_secret: string;
}

/**
 * Ceiling on AppRecord.notes. Free text otherwise - this is a size limit, not a content rule, and it
 * is shared so the console's character counter and the API's schema cannot disagree.
 */
export const MAX_NOTES_LENGTH = 2000;

/**
 * `checkMemberGroups` accepts at most 20 group IDs per request.
 * @see https://learn.microsoft.com/en-us/graph/api/directoryobject-checkmembergroups
 */
export const MAX_GROUP_IDS = 20;

/** Custom attribute holding the Entra object ID, mapped from the `oid` claim by the OIDC IdP. */
export const ATTR_ENTRA_OID = 'custom:entra_oid';

/**
 * OAuth scopes granted to every broker app client.
 *
 * `aws.cognito.signin.user.admin` is deliberately absent and must stay that way. That scope
 * authorizes the Cognito `UpdateUserAttributes` API against the caller's own profile, which would
 * let a signed-in user rewrite their own `custom:entra_oid` to another directory user's object ID
 * and have the authorization trigger evaluate group membership as that person.
 *
 * The attribute cannot be made immutable instead: Cognito rewrites IdP-mapped attributes on every
 * federated sign-in, and immutable attributes are writable only at user creation, so sign-in would
 * break on the user's second login. Withholding the scope is the control.
 */
export const APP_CLIENT_OAUTH_SCOPES = ['openid', 'email', 'profile'] as const;

/** The scope that must never be granted. See APP_CLIENT_OAUTH_SCOPES. */
export const FORBIDDEN_OAUTH_SCOPE = 'aws.cognito.signin.user.admin';

/**
 * Guards the invariant above at the point of use, so a future edit to the scope list fails loudly
 * instead of silently opening a privilege-escalation path.
 */
export function assertScopesSafe(scopes: readonly string[]): string[] {
  if (scopes.includes(FORBIDDEN_OAUTH_SCOPE)) {
    throw new Error(
      `Refusing to grant ${FORBIDDEN_OAUTH_SCOPE}: it permits UpdateUserAttributes, which would let ` +
        `a user rewrite their own ${ATTR_ENTRA_OID} and impersonate another directory user during ` +
        'the group-membership check.',
    );
  }
  return [...scopes];
}

/** Custom attribute holding the Entra UPN, mapped from `preferred_username`. */
export const ATTR_ENTRA_UPN = 'custom:entra_upn';
/** Claim injected on allow: the matched group ID, or "none" when the app requires no groups. */
export const CLAIM_AUTHORIZED_GROUP = 'custom:authorized_group';
/** Claim injected on allow: the registry app_name that authorized this token. */
export const CLAIM_BROKER_APP = 'custom:broker_app';
